import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  CheckIcon,
  PlusIcon,
  Popover,
  PopoverButton,
  PopoverContent,
  PopoverTrigger,
  StudioIcon,
  Tooltip,
  cn,
} from '@playerbase/ui';

import { useAuth } from '../../auth/use-auth.js';
import { SidebarNavButton } from './sidebar-nav-button.js';

type StudioSwitcherProps = {
  collapsed?: boolean;
};

export function StudioSwitcher({
  collapsed = false,
}: StudioSwitcherProps): JSX.Element | null {
  const { studios, activeStudioId, setActiveStudioId } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (studios.length === 0) return null;

  const active = studios.find((s) => s.id === activeStudioId) ?? studios[0];
  if (active === undefined) return null;
  // Capturing the shortId here keeps it inside the narrowed scope; the
  // function declarations below otherwise lose `active`'s narrowing.
  const activeShortId = active.shortId;

  function selectStudio(studioId: string, shortId: string): void {
    // TODO(app-shell): when the first epic that introduces persistent draft
    // state lands (likely Feedback Settings), prompt a confirm-discard dialog
    // before switching studios so unsaved work is not silently dropped.
    setActiveStudioId(studioId);
    setOpen(false);
    navigate(`/s/${shortId}`);
  }

  function goToNewStudio(): void {
    setOpen(false);
    navigate('/studios/new');
  }

  function goToManageStudio(): void {
    setOpen(false);
    navigate(`/s/${activeShortId}/studio-settings`);
  }

  const popoverContent = (
    <PopoverContent
      className="w-[15rem]"
      side={collapsed ? 'right' : 'top'}
      align={collapsed ? 'end' : 'start'}
    >
      <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-text-muted">
        Studios
      </div>
      {studios.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => selectStudio(s.id, s.shortId)}
          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-2"
        >
          <span className="flex-1 truncate">{s.name}</span>
          {s.id === active.id && <CheckIcon className="text-accent" />}
        </button>
      ))}
      <div className="my-1 border-t border-border" />
      <button
        type="button"
        onClick={goToNewStudio}
        className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-xs text-text-muted hover:bg-surface-2 hover:text-text"
      >
        <PlusIcon />
        <span>Create new studio</span>
      </button>
      {collapsed && (
        <>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={goToManageStudio}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-2"
          >
            <StudioIcon />
            <span>Manage studio</span>
          </button>
        </>
      )}
    </PopoverContent>
  );

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 pt-2 pb-3">
        <Popover open={open} onOpenChange={setOpen}>
          <Tooltip content={active.name} side="right" delayMs={120}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Studio: ${active.name}`}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-md',
                  'text-text transition-colors hover:bg-surface-2',
                )}
              >
                {/* Outlined / transparent treatment so the studio chip
                    reads distinctly from the filled profile avatar that
                    sits directly below it. */}
                <Avatar
                  name={active.name}
                  size="md"
                  className="bg-transparent border border-border-strong text-text-muted"
                />
              </button>
            </PopoverTrigger>
          </Tooltip>
          {popoverContent}
        </Popover>
      </div>
    );
  }

  return (
    <>
      <div className="px-3 pt-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <PopoverButton>
              <div className="flex flex-1 flex-col leading-tight">
                <span className="font-mono text-[9px] uppercase tracking-wider text-text-muted">
                  Studio
                </span>
                <span className="truncate">{active.name}</span>
              </div>
            </PopoverButton>
          </PopoverTrigger>
          {popoverContent}
        </Popover>
      </div>
      <div className="px-2 pt-1 pb-1">
        <SidebarNavButton
          to={`/s/${active.shortId}/studio-settings`}
          icon={<StudioIcon />}
          label="Manage studio"
        />
      </div>
    </>
  );
}
